import React, { useState, useCallback } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { API_BASE_URL } from '../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const POPULAR_ROUTES = [
  { label: 'SIN → JED', dep: 'SIN', arr: 'JED', note: 'Singapore → Jeddah' },
  { label: 'SIN → MED', dep: 'SIN', arr: 'MED', note: 'Singapore → Madinah' },
  { label: 'KUL → JED', dep: 'KUL', arr: 'JED', note: 'Kuala Lumpur → Jeddah' },
  { label: 'CGK → JED', dep: 'CGK', arr: 'JED', note: 'Jakarta → Jeddah' },
  { label: 'DXB → JED', dep: 'DXB', arr: 'JED', note: 'Dubai → Jeddah' },
];

const UMRAH_AIRLINES = [
  { label: 'Saudia (SV)', iata: 'SV' },
  { label: 'Singapore Airlines (SQ)', iata: 'SQ' },
  { label: 'Emirates (EK)', iata: 'EK' },
  { label: 'Malaysia Airlines (MH)', iata: 'MH' },
  { label: 'Garuda Indonesia (GA)', iata: 'GA' },
  { label: 'flynas (XY)', iata: 'XY' },
  { label: 'flydubai (FZ)', iata: 'FZ' },
  { label: 'Batik Air (ID)', iata: 'ID' },
];

const STATUS_STYLES = {
  scheduled:  { bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Scheduled' },
  active:     { bg: 'bg-emerald-100', text: 'text-emerald-700',dot: 'bg-emerald-500', label: 'In Air' },
  landed:     { bg: 'bg-green-100',   text: 'text-green-700',  dot: 'bg-green-500',  label: 'Landed' },
  cancelled:  { bg: 'bg-red-100',     text: 'text-red-700',    dot: 'bg-red-500',    label: 'Cancelled' },
  incident:   { bg: 'bg-orange-100',  text: 'text-orange-700', dot: 'bg-orange-500', label: 'Incident' },
  diverted:   { bg: 'bg-yellow-100',  text: 'text-yellow-700', dot: 'bg-yellow-500', label: 'Diverted' },
  unknown:    { bg: 'bg-sand',        text: 'text-ink/60',     dot: 'bg-ink/30',     label: 'Unknown' },
};

const getStatus = (key) => STATUS_STYLES[key?.toLowerCase()] || STATUS_STYLES.unknown;

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return '—'; }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const delayBadge = (mins) => {
  if (!mins || mins <= 0) return null;
  const color = mins > 60 ? 'text-red-600' : 'text-orange-500';
  return <span className={`text-xs font-semibold ${color}`}>+{mins}m delay</span>;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionEyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="ornament"><i /><span /><i /></span>
    <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-primary">{children}</p>
  </div>
);

const TimeBlock = ({ label, scheduled, estimated, actual, terminal, gate, delay }) => {
  const display = actual || estimated || scheduled;
  return (
    <div className="flex-1 min-w-0">
      <p className="text-[11px] tracking-widest uppercase text-ink/40 mb-1">{label}</p>
      <p className="font-display text-2xl sm:text-3xl text-ink leading-none">{fmt(display)}</p>
      {estimated && estimated !== scheduled && (
        <p className="text-[11px] text-ink/50 mt-0.5">Est: {fmt(estimated)}</p>
      )}
      {scheduled && (
        <p className="text-[11px] text-ink/40">{fmtDate(scheduled)}</p>
      )}
      <div className="flex flex-wrap gap-2 mt-1.5">
        {terminal && terminal !== '—' && (
          <span className="text-[10px] bg-sand text-ink/60 px-2 py-0.5 rounded-full">
            Terminal {terminal}
          </span>
        )}
        {gate && gate !== '—' && (
          <span className="text-[10px] bg-sand text-ink/60 px-2 py-0.5 rounded-full">
            Gate {gate}
          </span>
        )}
        {delayBadge(delay)}
      </div>
    </div>
  );
};

const FlightCard = ({ flight, index }) => {
  const [expanded, setExpanded] = useState(false);
  const st = getStatus(flight.status);

  return (
    <Reveal delay={index * 60}>
      <div className="lux-card rounded-lg border border-sand bg-white overflow-hidden">
        {/* Card Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5">
          {/* Airline + Flight number */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-lg">✈️</span>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-ink leading-tight truncate">
                {flight.airline_name}
              </p>
              <p className="text-sm text-ink/50">{flight.flight_number}</p>
            </div>
          </div>

          {/* Route summary */}
          <div className="flex items-center gap-2 sm:gap-4 flex-1 justify-center">
            <div className="text-center">
              <p className="font-display text-xl sm:text-2xl text-ink">{flight.dep_iata}</p>
              <p className="text-[11px] text-ink/50 max-w-[80px] truncate">{flight.dep_airport}</p>
            </div>
            <div className="flex flex-col items-center gap-0.5 px-2">
              <div className="w-16 sm:w-24 h-px bg-sand relative">
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-accent text-xs">✈</div>
              </div>
              <p className="text-[10px] text-ink/40 tracking-wider uppercase">Direct</p>
            </div>
            <div className="text-center">
              <p className="font-display text-xl sm:text-2xl text-ink">{flight.arr_iata}</p>
              <p className="text-[11px] text-ink/50 max-w-[80px] truncate">{flight.arr_airport}</p>
            </div>
          </div>

          {/* Status + toggle */}
          <div className="flex items-center gap-3 shrink-0">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
              {st.label}
            </span>
            <button
              onClick={() => setExpanded((p) => !p)}
              aria-expanded={expanded}
              className="text-ink/40 hover:text-primary transition-colors p-1"
              aria-label="Toggle details"
            >
              <svg className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-sand px-4 sm:px-5 py-4 bg-cream/50 animate-fade-in">
            <div className="flex flex-col sm:flex-row gap-6">
              <TimeBlock
                label="Departure"
                scheduled={flight.dep_scheduled}
                estimated={flight.dep_estimated}
                actual={flight.dep_actual}
                terminal={flight.dep_terminal}
                gate={flight.dep_gate}
                delay={flight.dep_delay}
              />
              <div className="hidden sm:flex items-center self-center">
                <div className="flex flex-col items-center gap-1 px-4">
                  <div className="w-px h-12 bg-sand" />
                  <span className="text-accent text-lg">✈</span>
                  <div className="w-px h-12 bg-sand" />
                </div>
              </div>
              <TimeBlock
                label="Arrival"
                scheduled={flight.arr_scheduled}
                estimated={flight.arr_estimated}
                actual={flight.arr_actual}
                terminal={flight.arr_terminal}
                gate={flight.arr_gate}
                delay={flight.arr_delay}
              />
            </div>
          </div>
        )}
      </div>
    </Reveal>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const FlightSchedulePage = ({ navigate }) => {
  const [searchType, setSearchType] = useState('route'); // 'route' | 'flight' | 'airline'
  const [form, setForm] = useState({
    dep_iata: '',
    arr_iata: '',
    flight_iata: '',
    airline_iata: '',
    flight_date: '',
    flight_status: '',
  });
  const [flights, setFlights]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError]       = useState(null); // { type, message }

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value.toUpperCase().trim() }));
  };

  const handleDateChange = (e) => {
    setForm((prev) => ({ ...prev, flight_date: e.target.value }));
  };

  const handleStatusChange = (e) => {
    setForm((prev) => ({ ...prev, flight_status: e.target.value }));
  };

  const applyRoute = (route) => {
    setSearchType('route');
    setForm((prev) => ({ ...prev, dep_iata: route.dep, arr_iata: route.arr }));
  };

  const applyAirline = (iata) => {
    setSearchType('airline');
    setForm((prev) => ({ ...prev, airline_iata: iata }));
  };

  const buildParams = useCallback(() => {
    const p = {};
    if (searchType === 'route') {
      if (form.dep_iata)  p.dep_iata  = form.dep_iata;
      if (form.arr_iata)  p.arr_iata  = form.arr_iata;
    } else if (searchType === 'flight') {
      if (form.flight_iata) p.flight_iata = form.flight_iata;
    } else if (searchType === 'airline') {
      if (form.airline_iata) p.airline_iata = form.airline_iata;
    }
    if (form.flight_date)   p.flight_date   = form.flight_date;
    if (form.flight_status) p.flight_status = form.flight_status;
    p.limit = '15';
    return new URLSearchParams(p).toString();
  }, [searchType, form]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    setError(null);
    setFlights([]);
    setSearched(false);

    // Basic validation
    if (searchType === 'route' && !form.dep_iata && !form.arr_iata) {
      setError({ type: 'validation', message: 'Please enter at least a departure or arrival airport code (e.g. SIN or JED).' });
      return;
    }
    if (searchType === 'flight' && !form.flight_iata) {
      setError({ type: 'validation', message: 'Please enter a flight number — for example SQ321 or EK348.' });
      return;
    }
    if (searchType === 'airline' && !form.airline_iata) {
      setError({ type: 'validation', message: 'Please enter an airline IATA code — for example SQ for Singapore Airlines.' });
      return;
    }

    setLoading(true);
    try {
      const qs = buildParams();
      let res, data;
      try {
        res = await fetch(`${API_BASE_URL}/flights/?${qs}`);
      } catch (networkErr) {
        setError({ type: 'network', message: 'Unable to connect. Please check your internet connection and try again.' });
        setLoading(false);
        return;
      }

      // Try to parse JSON — server may return HTML on crash
      const text = await res.text();
      try {
        data = JSON.parse(text);
      } catch {
        if (res.status === 500) {
          setError({ type: 'server', message: 'The server encountered an error. This is likely a configuration issue — please contact support.' });
        } else {
          setError({ type: 'server', message: `Unexpected server response (${res.status}). Please try again later.` });
        }
        setLoading(false);
        return;
      }

      if (!res.ok) {
        const msg = data?.error || 'Failed to fetch flights.';
        let type = 'unknown';
        if (res.status === 503) type = 'not_configured';
        else if (res.status === 502) type = 'provider';
        else if (res.status === 429) type = 'api_limit';
        else if (res.status >= 500) type = 'server';
        setError({ type, message: msg });
        setLoading(false);
        return;
      }

      setFlights(data.flights || []);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen pb-[88px] min-[901px]:pb-0">
      <Navbar navigate={navigate} />

      {/* ── Hero Banner ── */}
      <section className="relative bg-ink pt-28 pb-16 sm:pt-32 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C9A45C 0, transparent 40%), radial-gradient(circle at 80% 30%, #C9A45C 0, transparent 35%)' }} />
        <div className="absolute top-8 right-12 w-20 h-20 border border-accent/20 rotate-45 hidden md:block" />
        <div className="absolute bottom-8 left-16 w-10 h-10 border border-accent/15 rotate-45 hidden lg:block" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <SectionEyebrow>Flight Schedules</SectionEyebrow>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-white mt-2 leading-tight">
            Check Your <span className="italic text-accent">Flight Times</span>
          </h1>
          <p className="text-white/70 mt-4 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Search real-time departure and arrival schedules for Umrah routes from Singapore and beyond.
          </p>
        </div>
      </section>

      {/* ── Search Form ── */}
      <section className="max-w-4xl mx-auto px-4 -mt-8 relative z-10">
        <div className="bg-white rounded-xl shadow-2xl border border-sand overflow-hidden">

          {/* Tab switcher */}
          <div className="flex border-b border-sand">
            {[
              { key: 'route',   icon: '🛫', label: 'By Route' },
              { key: 'flight',  icon: '✈️',  label: 'By Flight No.' },
              { key: 'airline', icon: '🏢', label: 'By Airline' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSearchType(tab.key)}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all border-b-2 ${
                  searchType === tab.key
                    ? 'border-accent text-primary bg-cream/60'
                    : 'border-transparent text-ink/50 hover:text-ink hover:bg-cream/30'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="p-5 sm:p-6 space-y-4">

            {/* Route fields */}
            {searchType === 'route' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-ink/50 font-semibold mb-1.5 block">
                    From (IATA)
                  </label>
                  <input
                    name="dep_iata"
                    value={form.dep_iata}
                    onChange={handleChange}
                    placeholder="e.g. SIN"
                    maxLength={3}
                    className="w-full border border-sand rounded-sm px-4 py-3 text-ink font-semibold tracking-widest focus:border-accent focus:outline-none transition-colors uppercase placeholder:font-normal placeholder:tracking-normal placeholder:text-ink/30 text-lg"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-ink/50 font-semibold mb-1.5 block">
                    To (IATA)
                  </label>
                  <input
                    name="arr_iata"
                    value={form.arr_iata}
                    onChange={handleChange}
                    placeholder="e.g. JED"
                    maxLength={3}
                    className="w-full border border-sand rounded-sm px-4 py-3 text-ink font-semibold tracking-widest focus:border-accent focus:outline-none transition-colors uppercase placeholder:font-normal placeholder:tracking-normal placeholder:text-ink/30 text-lg"
                  />
                </div>
              </div>
            )}

            {/* Flight number field */}
            {searchType === 'flight' && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink/50 font-semibold mb-1.5 block">
                  Flight Number
                </label>
                <input
                  name="flight_iata"
                  value={form.flight_iata}
                  onChange={handleChange}
                  placeholder="e.g. SQ321"
                  maxLength={7}
                  className="w-full border border-sand rounded-sm px-4 py-3 text-ink font-semibold tracking-widest focus:border-accent focus:outline-none transition-colors uppercase placeholder:font-normal placeholder:tracking-normal placeholder:text-ink/30 text-lg"
                />
              </div>
            )}

            {/* Airline field */}
            {searchType === 'airline' && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink/50 font-semibold mb-1.5 block">
                  Airline IATA Code
                </label>
                <input
                  name="airline_iata"
                  value={form.airline_iata}
                  onChange={handleChange}
                  placeholder="e.g. SQ"
                  maxLength={3}
                  className="w-full border border-sand rounded-sm px-4 py-3 text-ink font-semibold tracking-widest focus:border-accent focus:outline-none transition-colors uppercase placeholder:font-normal placeholder:tracking-normal placeholder:text-ink/30 text-lg"
                />
              </div>
            )}

            {/* Optional filters row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink/50 font-semibold mb-1.5 block">
                  Date (optional)
                </label>
                <input
                  type="date"
                  name="flight_date"
                  value={form.flight_date}
                  onChange={handleDateChange}
                  className="w-full border border-sand rounded-sm px-4 py-3 text-ink focus:border-accent focus:outline-none transition-colors text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-ink/50 font-semibold mb-1.5 block">
                  Status (optional)
                </label>
                <select
                  name="flight_status"
                  value={form.flight_status}
                  onChange={handleStatusChange}
                  className="w-full border border-sand rounded-sm px-4 py-3 text-ink focus:border-accent focus:outline-none transition-colors text-sm bg-white"
                >
                  <option value="">Any status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="active">In Air</option>
                  <option value="landed">Landed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className={`flex items-start gap-3 rounded-lg px-4 py-3 border text-sm ${
                error.type === 'validation'
                  ? 'bg-amber-50 border-amber-300'
                  : error.type === 'network'
                  ? 'bg-red-50 border-red-200'
                  : error.type === 'not_configured'
                  ? 'bg-purple-50 border-purple-200'
                  : error.type === 'api_limit'
                  ? 'bg-orange-50 border-orange-300'
                  : 'bg-red-50 border-red-200'
              }`}>
                <span className="text-lg shrink-0 mt-0.5">
                  {error.type === 'validation'    ? '✏️'
                  : error.type === 'network'      ? '📡'
                  : error.type === 'not_configured'? '🔧'
                  : error.type === 'api_limit'    ? '⏳'
                  : error.type === 'provider'     ? '🛰️'
                  : '⚠️'}
                </span>
                <div>
                  <p className={`font-semibold mb-0.5 ${
                    error.type === 'validation'      ? 'text-amber-800'
                    : error.type === 'not_configured'? 'text-purple-800'
                    : error.type === 'api_limit'     ? 'text-orange-800'
                    : 'text-red-700'
                  }`}>
                    {error.type === 'validation'      ? 'Check your input'
                    : error.type === 'network'        ? 'No internet connection'
                    : error.type === 'not_configured' ? 'Service not configured'
                    : error.type === 'api_limit'      ? 'API limit reached'
                    : error.type === 'provider'       ? 'Flight data unavailable'
                    : error.type === 'server'         ? 'Server error'
                    : 'Something went wrong'}
                  </p>
                  <p className={`text-xs leading-relaxed ${
                    error.type === 'validation'      ? 'text-amber-700'
                    : error.type === 'not_configured'? 'text-purple-700'
                    : error.type === 'api_limit'     ? 'text-orange-700'
                    : 'text-red-600'
                  }`}>
                    {error.message}
                  </p>
                  {error.type === 'network' && (
                    <button
                      type="button"
                      onClick={handleSearch}
                      className="mt-2 text-xs font-semibold text-red-700 underline hover:no-underline"
                    >
                      Try again
                    </button>
                  )}
                  {error.type === 'not_configured' && (
                    <p className="mt-1 text-[11px] text-purple-600">
                      Please contact our team for assistance with flight information.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Search button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-lux gradient-gold text-ink w-full py-4 rounded-sm font-semibold text-base tracking-wide flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
            >
              {loading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Searching flights…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search Flights
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* ── Quick-access strips ── */}
      <section className="max-w-4xl mx-auto px-4 mt-8">
        <Reveal>
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.22em] text-ink/40 font-semibold mb-3">
              Popular Umrah Routes
            </p>
            <div className="flex flex-wrap gap-2">
              {POPULAR_ROUTES.map((r) => (
                <button
                  key={r.label}
                  onClick={() => applyRoute(r)}
                  title={r.note}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-sand hover:border-accent text-sm font-semibold text-ink/70 hover:text-primary rounded-full transition-all bg-white"
                >
                  <span className="text-accent text-xs">✈</span> {r.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-ink/40 font-semibold mb-3">
              Umrah Airlines
            </p>
            <div className="flex flex-wrap gap-2">
              {UMRAH_AIRLINES.map((a) => (
                <button
                  key={a.iata}
                  onClick={() => applyAirline(a.iata)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-sand hover:border-accent text-sm font-semibold text-ink/70 hover:text-primary rounded-full transition-all bg-white"
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── Results ── */}
      <section className="max-w-4xl mx-auto px-4 mt-10 pb-16">

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-sand p-5 animate-pulse">
                <div className="flex gap-4 items-center">
                  <div className="w-10 h-10 bg-sand rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-sand rounded w-1/3" />
                    <div className="h-3 bg-sand rounded w-1/4" />
                  </div>
                  <div className="w-24 h-6 bg-sand rounded-full" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No results */}
        {!loading && searched && flights.length === 0 && (
          <Reveal>
            <div className="text-center py-16 text-ink/50">
              <div className="text-5xl mb-4">🔍</div>
              <p className="text-lg font-semibold text-ink mb-1">No flights found</p>
              <p className="text-sm">Try adjusting your search — a different date or route may return results.</p>
            </div>
          </Reveal>
        )}

        {/* Flight cards */}
        {!loading && flights.length > 0 && (
          <>
            <Reveal>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-ink/60">
                  <span className="font-semibold text-primary text-base">{flights.length}</span> flight{flights.length !== 1 ? 's' : ''} found
                </p>
                <p className="text-[11px] text-ink/40 uppercase tracking-wider">Click a card for full details</p>
              </div>
            </Reveal>
            <div className="space-y-3">
              {flights.map((flight, idx) => (
                <FlightCard key={`${flight.flight_number}-${idx}`} flight={flight} index={idx} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* ── Info footer note ── */}
      <section className="bg-ink py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-white/40 text-xs leading-relaxed">
            Flight data is sourced from AviationStack and is provided for informational purposes only.
            Always verify with your airline before travel. Data may be delayed by up to 30 minutes.
          </p>
          <button
            onClick={() => navigate('contact')}
            className="mt-4 text-accent text-sm font-semibold hover:underline"
          >
            Need help booking? Contact our team →
          </button>
        </div>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
};

export default FlightSchedulePage;
